import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { buildApiUrl } from '@/lib/api/base-url';
import { getAccessToken } from '@/lib/auth/token-storage';
import { queryKeys } from '@/lib/query/query-keys';
import type { TripDetailCostDocument } from '@/hooks/trips/types';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

function mapUploadedDocument(record: unknown): TripDetailCostDocument | null {
  if (!record || typeof record !== 'object') {
    return null;
  }

  const data = record as Record<string, unknown>;
  const id = Number(data.id ?? 0);
  const name = typeof data.filename === 'string' ? data.filename : '';
  const mimetype = typeof data.mimetype === 'string' ? (data.mimetype as string) : undefined;
  const sizeValue = Number(data.size ?? Number.NaN);
  const size = Number.isFinite(sizeValue) ? sizeValue : undefined;
  const sizeHuman =
    typeof data.size_human === 'string' && data.size_human.trim().length > 0
      ? (data.size_human as string)
      : undefined;

  if (!name || !Number.isFinite(id)) {
    return null;
  }

  return {
    id,
    name,
    mimetype,
    size,
    sizeHuman,
  };
}

interface UploadDocumentsVariables {
  files: File[];
}

async function uploadDocuments({
  files,
}: UploadDocumentsVariables): Promise<TripDetailCostDocument[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append('ufile', file));

  const response = await apiFetch('/api/v1/documents', {
    method: 'POST',
    body: formData,
  });

  const json = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(json, 'Failed to upload documents');
    throw new Error(message);
  }

  if (!Array.isArray(json)) {
    return [];
  }

  return json
    .map(mapUploadedDocument)
    .filter((value): value is TripDetailCostDocument => Boolean(value));
}

export function useUploadDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadDocuments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents });
    },
    onError: (error) => {
      console.error('Error uploading documents:', error);
    },
  });
}

interface DeleteDocumentVariables {
  documentId: number;
}

async function deleteDocument({ documentId }: DeleteDocumentVariables): Promise<void> {
  const response = await apiFetch(`/api/v1/documents/${documentId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message = extractErrorMessage(payload, 'Failed to delete document');
    throw new Error(message);
  }
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents });
    },
    onError: (error) => {
      console.error('Error deleting document:', error);
    },
  });
}

interface DocumentUrlOptions {
  filename?: string;
  download?: boolean;
}

/**
 * Builds the URL for viewing or downloading a document.
 * @param documentId - The ID of the document
 * @param options - Optional parameters for filename and download behavior
 * @returns The full URL to the document endpoint
 */
export function getDocumentUrl(documentId: number, options?: DocumentUrlOptions): string {
  const params = new URLSearchParams();

  if (options?.filename) {
    params.set('filename', options.filename);
  }

  if (options?.download) {
    params.set('download', 'true');
  }

  const queryString = params.toString();
  const path = `/api/v1/documents/${documentId}${queryString ? `?${queryString}` : ''}`;

  return buildApiUrl(path);
}

type DocumentActionType = 'view' | 'download';

interface LoadingDocumentState {
  documentId: number;
  action: DocumentActionType;
}

/**
 * Hook for document viewing and downloading.
 * Returns functions to view (open in browser) or download a document,
 * along with loading state tracking.
 */
export function useDocumentActions() {
  const [loadingDocument, setLoadingDocument] = useState<LoadingDocumentState | null>(null);

  const viewDocument = useCallback(
    async (documentId: number, filename?: string) => {
      if (loadingDocument) return; // Prevent concurrent actions

      setLoadingDocument({ documentId, action: 'view' });
      try {
        const token = getAccessToken();
        const url = getDocumentUrl(documentId, { filename, download: false });
        await fetchAndOpenDocument(url, token, false, filename);
      } finally {
        setLoadingDocument(null);
      }
    },
    [loadingDocument]
  );

  const downloadDocument = useCallback(
    async (documentId: number, filename?: string) => {
      if (loadingDocument) return; // Prevent concurrent actions

      setLoadingDocument({ documentId, action: 'download' });
      try {
        const token = getAccessToken();
        const url = getDocumentUrl(documentId, { filename, download: true });
        await fetchAndOpenDocument(url, token, true, filename);
      } finally {
        setLoadingDocument(null);
      }
    },
    [loadingDocument]
  );

  const isDocumentLoading = useCallback(
    (documentId: number, action?: DocumentActionType) => {
      if (!loadingDocument) return false;
      if (loadingDocument.documentId !== documentId) return false;
      if (action && loadingDocument.action !== action) return false;
      return true;
    },
    [loadingDocument]
  );

  return {
    viewDocument,
    downloadDocument,
    isDocumentLoading,
    isAnyDocumentLoading: loadingDocument !== null,
  };
}

async function fetchAndOpenDocument(
  url: string,
  token: string | null,
  forceDownload: boolean,
  filename?: string
): Promise<void> {
  try {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'same-origin',
    });

    if (!response.ok) {
      console.error('Failed to fetch document:', response.status, response.statusText);
      return;
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    if (forceDownload) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename ?? 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Open in new tab for viewing
      window.open(blobUrl, '_blank');
    }

    // Clean up the blob URL after a delay to ensure download/view completes
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 10000);
  } catch (error) {
    console.error('Error fetching document:', error);
  }
}
